import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { groupBy, startCase } from "es-toolkit";

import {
  createDividerOption,
  ellipsise,
  FONT_WEIGHT_SEMI_BOLD,
  optionize,
  Select,
  SkiliticsIcon,
  TableCell,
  TableRow,
  useMapTypeIdToText,
  type Option,
} from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

import type { LinkageToResolve } from "./types";
import { useMapTypeIdToTarget } from "./useMapTypeIdToTarget";

const MissingItem = styled.div`
  display: flex;
  flex-direction: column;

  width: 200px;
  overflow: hidden;
`;

const MissingItemReference = styled.span`
  font-size: ${({ theme }) => theme.fontSizeSmall};
  ${ellipsise}
`;

const MissingItemTitle = styled.span`
  font-size: ${({ theme }) => theme.fontSizeSmall};
  font-weight: ${FONT_WEIGHT_SEMI_BOLD};
  ${ellipsise}
`;

const BodyTableCell = styled(TableCell)`
  padding-block: 10px;
  border-block-end: solid 1px ${({ theme }) => theme.outlineSoft};
`;

const Selection = styled(Select)`
  width: 200px;

  &::before {
    border-color: transparent;
  }

  &&:hover::before {
    border-color: transparent;
  }

  > div {
    padding: 0;
  }
`;

const Target = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

type Props = {
  linkage: LinkageToResolve & { candidateId?: number };
  onSelected: (candidateId: number) => void;
};

export const LinkageToBeResolved = ({ linkage, onSelected }: Props) => {
  const theme = useTheme();
  const formatMessage = useFormatMessage(formatStrings);

  const mapTypeIdToText = useMapTypeIdToText();
  const mapTypeIdToTarget = useMapTypeIdToTarget();

  const { nodeToLink, candidates, context } = linkage;

  const createSelectPlaceholder = (typeId: string) =>
    formatMessage("placeholder", { type: mapTypeIdToText(typeId).toLowerCase().replace("3d", "3D") });

  const onOptionSelected = ({ value }: Option) => {
    const candidateId = candidates.find((c) => c.title === value)?.id;

    if (candidateId !== undefined) {
      onSelected(candidateId);
    }
  };

  const selectedOption =
    linkage.candidateId !== undefined ? candidates.find((c) => c.id === linkage.candidateId)?.title : "";

  const groupedCandidates = groupBy(candidates, ({ grouping }) => grouping ?? formatMessage("activity"));

  const options = Object.keys(groupedCandidates).flatMap((grouping) => [
    createDividerOption(grouping),
    ...optionize(groupedCandidates[grouping].map(({ title }) => title)),
  ]);

  return (
    <TableRow key={nodeToLink.id}>
      <BodyTableCell sx={{ textAlign: "center", color: selectedOption ? theme.tertiaryStrong : theme.warning }}>
        <SkiliticsIcon icon={selectedOption ? "tick" : "warning-2"} size={16} />
      </BodyTableCell>
      <BodyTableCell>
        <MissingItem>
          <MissingItemReference>{context.map(startCase).join(" | ")}</MissingItemReference>
          <MissingItemTitle>{nodeToLink.title}</MissingItemTitle>
        </MissingItem>
      </BodyTableCell>
      <BodyTableCell>
        <Target>{mapTypeIdToTarget(nodeToLink.typeId)}</Target>
      </BodyTableCell>
      <BodyTableCell sx={{ width: "200px" }}>
        <Selection
          value={selectedOption}
          options={options}
          placeholder={createSelectPlaceholder(nodeToLink.typeId)}
          onSelectedOptionChange={onOptionSelected}
        />
      </BodyTableCell>
    </TableRow>
  );
};

const formatStrings = {
  placeholder: {
    en: "Select {type}...",
  },
  activity: {
    en: "Activity",
  },
} satisfies FormatMessages;
